import { useMutation } from "@apollo/client";
import {
  InputPasswordControl,
  PasswordList,
  usePasswordPolicy,
} from "@aurem/components";
import { Button, Text } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContainer } from "components/AuthContainer";
import ROUTES, { PasswordResetParams } from "const/routes";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { gql } from "types";
import { useSignOut } from "utils/auth";
import * as z from "zod";

const RESET_PASSWORD = gql(/* GraphQL */ `
  mutation ResetPassword($requestId: String!, $newPassword: String!) {
    resetPassword(requestId: $requestId, newPassword: $newPassword) {
      accessToken
      refreshToken
    }
  }
`);

const ResetPassword: FC = () => {
  const { t } = useI18n();
  const { language } = useParams();
  const { requestId } = useParams<PasswordResetParams>() as PasswordResetParams;
  const { setAuthState } = useAuth();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const [resetPassword, { error, loading }] = useMutation(RESET_PASSWORD);
  const { validatePassword, policy } = usePasswordPolicy();

  const formSchema = z
    .object({
      password: z
        .string()
        .min(8, t("apps.admin.validation.password.minLength")),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("apps.admin.validation.password.match"),
      path: ["confirmPassword"],
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async ({ password }: z.infer<typeof formSchema>) => {
    try {
      validatePassword(password);
    } catch (err) {
      if (err instanceof Error) {
        form.setError("password", {
          type: "manual",
          message: err.message,
        });
        return;
      }
    }

    await signOut();
    const { data } = await resetPassword({
      variables: { newPassword: password, requestId },
    });
    const tokens = data?.resetPassword;
    if (tokens?.accessToken && tokens?.refreshToken) {
      setAuthState({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        status: AuthStatus.Authenticated,
      });
      await navigate("/" + ROUTES.ROOT);
    }
  };

  return (
    <FormProvider {...form}>
      <AuthContainer platform="admin">
        <h3 className="text-2xl">
          {t(
            "apps.admin.pages.passwordReset.requestId.page.resetPassword.title",
          )}
        </h3>

        <form
          className="flex flex-col gap-5"
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-5">
            <InputPasswordControl
              label={t(
                "apps.admin.pages.passwordReset.requestId.page.resetPassword.password.label",
              )}
              data-e2e-id="resetPassword__input__password"
              id="password"
              name="password"
              required
            />
            <InputPasswordControl
              label={t(
                "apps.admin.pages.passwordReset.requestId.page.resetPassword.confirmPassword.label",
              )}
              data-e2e-id="resetPassword__input__confirmPassword"
              id="confirmPassword"
              name="confirmPassword"
              required
            />

            {error && (
              <Text
                data-e2e-id="resetPassword__error__message"
                size="sm"
                weight="medium"
                className="text-danger-600"
              >
                {error.message}
              </Text>
            )}
          </div>

          <PasswordList
            policy={policy}
            translations={{
              intro: t("apps.admin.validation.list.intro"),
              capitalLetter: t("apps.admin.validation.list.capitalLetter"),
              lowercaseLetter: t("apps.admin.validation.list.lowercaseLetter"),
              number: t("apps.admin.validation.list.number"),
              specialCharacter: t(
                "apps.admin.validation.list.specialCharacter",
              ),
              minimumLength: t(
                "apps.admin.validation.list.minimumLength",
                policy?.minLength,
              ),
            }}
          />

          <div className="flex flex-col gap-2">
            <Button
              data-e2e-id="resetPassword__button__submit"
              disabled={loading}
              type="submit"
            >
              {t(
                "apps.admin.pages.passwordReset.requestId.page.resetPassword.submit",
              )}
            </Button>
            <Button
              as={Link}
              to={`/${language}/${ROUTES.SIGN_IN}`}
              variant="ghost"
              color="neutral"
            >
              {t(
                "apps.admin.pages.passwordReset.requestId.page.resetPassword.cancel",
              )}
            </Button>
          </div>
        </form>
      </AuthContainer>
    </FormProvider>
  );
};

export default ResetPassword;
